import { Injectable } from '@angular/core';
import { Observable, of, finalize, mergeMap, from as observableFrom } from 'rxjs';
import { Command, CommandBase, FilteredCommand, MultiselectCommand } from '../../../core/model/command';
import { ICommandHandler } from '../../../core/service/command/command-handler-resolver.service';
import { ComponentMessageDispatcher } from '../../../core/service/component-message-dispather.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { ProgressBarService } from '../../../core/service/progress-bar.service';
import { LeaseAgreementFilteredCommand } from '../../../rent/command/lease-agreement.command';
import { Contract } from '../../../rent/model/contract';
import { ISmsReceiver } from '../../../rent/service/base/contract';
import { BlockContractService } from '../../../rent/service/data/block-contract.service';
import { CooperativeHousingContractService } from '../../../rent/service/data/cooperative-housing-contract.service';
import { DwellingContractService } from '../../../rent/service/data/dwelling-contract.service';
import { OtherContractService } from '../../../rent/service/data/other-contract.service';
import { ParkingContractService } from '../../../rent/service/data/parking-contract.service';
import { PremisesContractService } from '../../../rent/service/data/premises-contract.service';
import { LeaseAgreementService } from '../../../rent/service/data/lease-agreement.service';
import { mailCommand } from '../../model/const/command';
import { LeaseAgreementCommandHandlerBase } from './lease-agreement-command-handler.base';

@Injectable()
export class SendTenantSMSCommandHandler extends LeaseAgreementCommandHandlerBase implements ICommandHandler {
  constructor(
    private readonly _messageDispatcher: ComponentMessageDispatcher,
    private readonly _messageService: MessageService,
    private readonly _navigation: NavigationService,
    private readonly _progressBar: ProgressBarService,
    dwellingService: DwellingContractService,
    parkingService: ParkingContractService,
    premisesService: PremisesContractService,
    otherService: OtherContractService,
    blockService: BlockContractService,
    cooperativeService: CooperativeHousingContractService,
    private leaseAgreementService: LeaseAgreementService
  ) {
    super(dwellingService, parkingService, premisesService, otherService, blockService, cooperativeService);
  }

  canExecute(command: CommandBase): boolean {
    return command.id == mailCommand.sendTenantSMS;
  }

  execute(command: CommandBase): Observable<boolean> {
    let tenants: ISmsReceiver[] = [];
    if (command instanceof MultiselectCommand) {
      tenants = command.items.filter(x => x.smsReceiver.length > 0);
    } else if (command instanceof FilteredCommand) {
      try {
        this._progressBar.start();

        const categoryCommand = command as LeaseAgreementFilteredCommand;
        var useLeaseAgreementController = categoryCommand.filterProvider.filterState.sendMessageType !== undefined;

        if (useLeaseAgreementController) {
          return this.leaseAgreementService
            .getSmsFiltered(
              command.filterProvider.filterState,
              command.includedIds,
              command.excludedIds,
              command.totalAffectedItemsControlValue
            )
            .pipe(
              mergeMap(tenants => this.dispatchAndNavigate(tenants)),
              finalize(() => this._progressBar.stop())
            );
        } else {
          const service = this._getService(categoryCommand.category);
          return service
            .getSmsFiltered(
              command.filterProvider.filterState,
              command.includedIds,
              command.excludedIds,
              command.totalAffectedItemsControlValue
            )
            .pipe(
              mergeMap(tenants => this.dispatchAndNavigate(tenants)),
              finalize(() => this._progressBar.stop())
            );
        }
      } catch (error) {
        //
        // Swallowing this used to leave the user with a stopped progress bar and no other sign
        // that the send was abandoned, because execute() then fell through to a dispatch with an
        // empty recipient list.
        //
        this._progressBar.stop();
        this._messageService.showError(error);
        return of(false);
      }
    } else if (command instanceof Command) {
      const contract = <Contract>command.context;
      tenants.push({
        id: contract.id,
        smsReceiver: `${contract.tenantOneName} ${contract.tenantOnePhone === undefined ? '' : contract.tenantOnePhone}`,
      });
    }
    return this.dispatchAndNavigate(tenants);
  }

  private dispatchAndNavigate(tenants: ISmsReceiver[]): Observable<boolean> {
    //
    // Returning nothing here is what made a failed or empty lookup look like a dead button:
    // CommandController.execute subscribes to whatever execute() returns, so undefined threw
    // inside the dispatcher and the form was never opened.
    //
    if (!tenants || tenants.length === 0) {
      this._messageService.showWarning('WARNING.NO_MESSAGE_RECIPIENTS');
      return of(false);
    }

    this._messageDispatcher.send({ tenants: tenants.map(item => ({ id: item.id, recipientName: item.smsReceiver })) });
    return observableFrom(this._navigation.navigate(['sms', 'sendsms']));
  }
}
